import LOCALSTORAGE_KEYS from '@web/consts/LOCALSTORAGE_KEYS'
import type { UserOnboarding } from '@web/store/types/modules/user'
import { clearUserScoped, readUserScoped, writeUserScoped } from './storage'

/**
 * Пустой объект считаем отсутствием данных: бэк присылает `onboarding: null`
 * или `{}` в ответах, где поле не заполняется, — такое значение не должно
 * ни сохраняться, ни перетирать уже сохранённое.
 *
 * Проверяем не просто «непустой объект», а что хотя бы одно из известных полей
 * `UserOnboarding` реально присутствует и имеет ожидаемый примитивный тип —
 * иначе испорченное значение из localStorage (например `{"foo":1}`) прошло бы
 * гвард и утекло бы наружу как валидный UserOnboarding.
 */
// Известное поле "пустое" (undefined ИЛИ null) — ок, бэк шлёт null для
// незаполненных полей (см. коммент выше), это не ошибка формы, а норма.
// Непустота всего объекта уже проверяется отдельно через has*-флаги ниже,
// так что здесь достаточно не отклонять null. Известное поле "заполнено" —
// считается валидным только с правильным примитивным типом, иначе это не
// валидный UserOnboarding, а мусор с тем же именем ключа.
function isValidNumberField (fieldValue: unknown): boolean {
  if (fieldValue === undefined || fieldValue === null) return true
  return typeof fieldValue === 'number'
}

function isValidRecordField (fieldValue: unknown): boolean {
  if (fieldValue === undefined || fieldValue === null) return true
  return typeof fieldValue === 'object' && !Array.isArray(fieldValue)
}

export function isNonEmptyOnboarding (value: unknown): value is UserOnboarding {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>

  const hasSuggestedStream = obj.suggestedUserCourseStream !== undefined && obj.suggestedUserCourseStream !== null
  const hasSelfieStream = obj.userCourseStreamWithSelfie !== undefined && obj.userCourseStreamWithSelfie !== null
  const hasRenewalPrices = obj.subscriptionRenewalPrices !== undefined && obj.subscriptionRenewalPrices !== null

  if (!hasSuggestedStream && !hasSelfieStream && !hasRenewalPrices) return false

  return isValidNumberField(obj.suggestedUserCourseStream) &&
    isValidNumberField(obj.userCourseStreamWithSelfie) &&
    isValidRecordField(obj.subscriptionRenewalPrices)
}

export function readOnboardingData (): UserOnboarding | null {
  return readUserScoped(LOCALSTORAGE_KEYS.ONBOARDING_DATA_PREFIX, isNonEmptyOnboarding)
}

export function writeOnboardingData (data: UserOnboarding): void {
  writeUserScoped(LOCALSTORAGE_KEYS.ONBOARDING_DATA_PREFIX, data)
}

export function clearOnboardingData (): void {
  clearUserScoped(LOCALSTORAGE_KEYS.ONBOARDING_DATA_PREFIX)
}
